import { QueryClient } from '@tanstack/query-core';

import { browserLogger } from '@widget/utils/datadog';

import type { QueryFunctionContext } from '@tanstack/react-query';
import type { ValidatePhoneResponse } from './types';
import type { ApiResponse } from '@widget/api/types';

export const ValidateQueryKey = {
  all: ['validate'] as const,
  phone: (phone: string) => [...ValidateQueryKey.all, 'phone', { phone }] as const,
};

export const ValidateQueryFn = {
  async phone(
    { queryKey }: QueryFunctionContext<ReturnType<typeof ValidateQueryKey.phone>>,
    timeout?: number,
  ) {
    let signal: AbortSignal | null = null;
    let timeoutId: NodeJS.Timeout | null = null;

    if (timeout) {
      const controller = new AbortController();
      timeoutId = setTimeout(
        () => controller.abort(new Error('Phone validation api timeout')),
        timeout,
      );
      signal = controller.signal;
    }

    const response = await fetch(`/api-widget/${queryKey[0]}/${queryKey[1]}`, {
      method: 'POST',
      body: JSON.stringify(queryKey[2]),
      headers: {
        'Content-Type': 'application/json',
      },
      signal,
    });

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    if (!response.ok) {
      throw new Error('Phone validation error response');
    }

    const body = (await response.json()) as ApiResponse<ValidatePhoneResponse>;

    if (body.error) {
      throw new Error('Phone validation invalid response');
    }

    browserLogger.log(`Phone validation success. { isValid: ${body.data.isValid} }`);

    return body.data;
  },
};

const client = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
      gcTime: Infinity,
    },
  },
});

export async function validatePhone(
  phone: string,
  timeout?: number,
): Promise<ValidatePhoneResponse | null> {
  try {
    return await client.fetchQuery({
      queryKey: ValidateQueryKey.phone(phone),
      queryFn: (context) => ValidateQueryFn.phone(context, timeout),
    });
  } catch (error) {
    browserLogger.error(error, 'Phone validation error');

    return null;
  }
}
